import { useEffect, useRef, useState } from "react";
import { saveProgress } from "../storage/progress";

// YouTube player with the app's own chrome (same UI as the Vidking embed):
// back button + title on top, seekbar with times, play/pause, ±10s skip,
// mute and fullscreen below. Powered by the official IFrame Player API, which
// exposes playback control across the origin boundary (the plain embed iframe
// cannot be scripted).
//
// YouTube's own overlays — title card at start, "More videos" card and logo —
// are covered by the corner gradient masks (see .yt-mask in styles.css); our
// chrome renders above them. The transparent .ytv-click layer toggles
// play/pause and stops clicks reaching YouTube's iframe.

let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        resolve(window.YT);
      };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return ytApiPromise;
}

// iPhone/iPad Safari has no element fullscreen; there the player is created
// with playsinline=0 ONCE, so playback always runs in iOS's native fullscreen
// player (the only real fullscreen iOS offers). Nothing is rebuilt afterwards:
// pause keeps the native player open, Done drops back to the page paused, and
// play reopens fullscreen. Android/desktop use the inline player + API
// fullscreen like every other provider.
const IS_IOS = typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ masquerades as desktop Safari
    (navigator.platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1));

function fmt(t) {
  const s = Math.max(0, Math.floor(t || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

const HIDE_MS = 3000;

// Gesture-tap zones: <30% width = back, >70% = forward, middle = play/pause.
const GESTURE_EDGE = 0.3;

// Icon set mirroring the Vidking embed's controls (stroke style, weights).
const Ico = {
  Play: () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
      <path d="M8 5.3v13.4c0 .8.9 1.3 1.6.9l10.4-6.7c.6-.4.6-1.4 0-1.8L9.6 4.4c-.7-.4-1.6.1-1.6.9z" />
    </svg>
  ),
  Pause: () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
      <rect x="6.6" y="5" width="3.6" height="14" rx="1" />
      <rect x="13.8" y="5" width="3.6" height="14" rx="1" />
    </svg>
  ),
  SkipBack: () => (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  SkipFwd: () => (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Vol: () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  ),
  VolX: () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  ),
  Full: () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ),

  Back: () => (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
};

export default function YoutubePlayer({ source, request, onBack }) {
  const containerRef = useRef(null);
  const pageRef = useRef(null);
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const seekRef = useRef(null);
  const draggingRef = useRef(false);
  const hideTimerRef = useRef(null);
  const lastSavedRef = useRef({ position: -10, at: 0 });
  const timeRef = useRef({ cur: 0, dur: 0 });
  // Seek target applied when a (re)created player becomes ready — set on first
  // mount (resume position) and when the fullscreen toggle rebuilds the player
  // so the native fullscreen player opens at the current spot.
  const resumeAtRef = useRef(Number(request.resumePositionSeconds || 0));
  // iOS can swallow the first autoplay after the fullscreen rebuild (the new
  // iframe's media element never saw a user gesture) — these retries re-kick
  // playVideo until playback actually starts.
  const autoplayRetryRef = useRef([]);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const volumeRef = useRef(100);
  const [time, setTime] = useState({ cur: 0, dur: 0 });
  const [chromeVisible, setChromeVisible] = useState(true);
  const [ripple, setRipple] = useState(null); // { side: "left"|"right", label }
  const lastTapRef = useRef({ t: 0, x: 0, y: 0, timer: null });
  const rippleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const videoId = source.videoId;

  // Progress persistence — same throttling as TorrentVideo's timeupdate path.
  function persist(ended, cur, dur) {
    const now = Date.now();
    const last = lastSavedRef.current;
    const position = ended ? dur : cur;
    if (!ended && now - last.at < 5000 && Math.abs(position - last.position) < 5) return;
    last.at = now;
    last.position = position;
    saveProgress({
      contentType: request.contentType,
      tmdbId: request.tmdbId,
      season: request.season,
      episode: request.episode,
      title: request.title,
      subtitle: request.subtitle,
      posterPath: request.posterPath,
      backdropPath: request.backdropPath,
      positionSeconds: position,
      durationSeconds: dur || 0,
      updatedAtMillis: now,
      completed: Boolean(ended),
    });
  }

  // Create the player. YT.Player replaces the mount node with an iframe, so
  // the mount div is appended imperatively inside a React-opaque wrapper.
  useEffect(() => {
    let disposed = false;
    setReady(false);
    setPlaying(false);
    loadYouTubeApi().then((YT) => {
      if (disposed || !mountRef.current) return;
      const host = document.createElement("div");
      mountRef.current.appendChild(host);
      const player = new YT.Player(host, {
        videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          // iOS: playsinline=0 from birth — playback hands itself to the OS
          // fullscreen player. Elsewhere: inline, with API fullscreen.
          playsinline: IS_IOS ? 0 : 1,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            if (disposed) return;
            setReady(true);
            try {
              const t = resumeAtRef.current;
              if (t >= 5) e.target.seekTo(t, true);
              resumeAtRef.current = 0;
              e.target.playVideo();
              // Retry shortly after — the first kick can be dropped when the
              // player was rebuilt outside a direct user gesture (iOS).
              autoplayRetryRef.current.forEach(clearTimeout);
              autoplayRetryRef.current = [400, 1200].map((ms) =>
                setTimeout(() => {
                  if (disposed) return;
                  const p = playerRef.current;
                  const st = p?.getPlayerState?.();
                  if (p && st !== 1 /* PLAYING */ && st !== 3 /* BUFFERING */) {
                    try { p.playVideo(); } catch { /* noop */ }
                  }
                }, ms),
              );
            } catch {
              /* autoplay can be blocked — the play button covers it */
            }
          },
          onStateChange: (e) => {
            if (disposed) return;
            setPlaying(e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING);
            if (e.data === YT.PlayerState.PLAYING) {
              autoplayRetryRef.current.forEach(clearTimeout);
              autoplayRetryRef.current = [];
            }
            // NOTE: pausing — including Done inside the native fullscreen
            // player — never rebuilds anything. iOS gives JS no way to tell
            // Done and pause apart, and yanking the user out of the native
            // player on every pause was the bug. The playsinline=0 player
            // stays mounted until the screen unmounts.
            if (e.data === YT.PlayerState.ENDED) persist(true, timeRef.current.cur, timeRef.current.dur);
          },
        },
      });
      playerRef.current = player;
    });
    return () => {
      disposed = true;
      // Save the final position before teardown.
      const { cur, dur } = timeRef.current;
      if (cur > 5) persist(false, cur, dur);
      try {
        playerRef.current?.destroy?.();
      } catch {
        /* already gone */
      }
      playerRef.current = null;
      if (mountRef.current) mountRef.current.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Clock + throttled progress save.
  useEffect(() => {
    if (!ready) return undefined;
    const iv = setInterval(() => {
      const p = playerRef.current;
      if (!p?.getCurrentTime) return;
      const cur = p.getCurrentTime() || 0;
      const dur = p.getDuration() || 0;
      timeRef.current = { cur, dur };
      setTime({ cur, dur });
      persist(false, cur, dur);
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // --- controls -----------------------------------------------------------

  // Activity marks: any pointer event stamps the clock and shows the bars.
  const showChrome = () => {
    lastActivityRef.current = Date.now();
    setChromeVisible(true);
  };

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  // Keyboard: ←/→ seek ±10s, ↑/↓ volume ±5, space play/pause.
  useEffect(() => {
    if (!ready) return undefined;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          skip(10);
          break;
        case "ArrowLeft":
          e.preventDefault();
          skip(-10);
          break;
        case "ArrowUp":
          e.preventDefault();
          changeVolume(volumeRef.current + 5);
          break;
        case "ArrowDown":
          e.preventDefault();
          changeVolume(volumeRef.current - 5);
          break;
        case " ":
          e.preventDefault();
          togglePlay();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Idle hider — a 1s watchdog instead of a one-shot timer, so buffering
  // pauses can't permanently suppress the fade: while PLAYING and idle past
  // HIDE_MS the bars fade; any other state (paused/buffering/ended) forces
  // them back so controls are always reachable.
  useEffect(() => {
    if (!ready) return undefined;
    const iv = setInterval(() => {
      const state = playerRef.current?.getPlayerState?.();
      if (state === 1 /* PLAYING */) {
        if (Date.now() - lastActivityRef.current >= HIDE_MS) setChromeVisible(false);
      } else {
        setChromeVisible(true);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [ready]);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    const state = p.getPlayerState?.();
    if (state === 1 /* PLAYING */ || state === 3 /* BUFFERING */) p.pauseVideo();
    else p.playVideo();
    showChrome();
  };

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    if (p.isMuted?.()) {
      p.unMute();
      if (p.setVolume && volume === 0) {
        p.setVolume(100);
        setVolume(100);
      }
      setMuted(false);
    } else {
      p.mute();
      setMuted(true);
    }
    showChrome();
  };

  const changeVolume = (val) => {
    const p = playerRef.current;
    const v = Math.min(100, Math.max(0, Math.round(val)));
    if (!p) return;
    p.setVolume(v);
    volumeRef.current = v;
    setVolume(v);
    if (v === 0) {
      p.mute();
      setMuted(true);
    } else if (p.isMuted?.()) {
      p.unMute();
      setMuted(false);
    }
    showChrome();
  };

  const volumeFromPointer = (el, clientX) => {
    const rect = el.getBoundingClientRect();
    changeVolume(((clientX - rect.left) / rect.width) * 100);
  };

  const skip = (delta) => {
    const p = playerRef.current;
    if (!p?.getCurrentTime) return;
    p.seekTo(Math.max(0, (p.getCurrentTime() || 0) + delta), true);
    showChrome();
  };

  // Fullscreen covers the WHOLE player page (top bar + stage + bottom bar)
  // so the controls stay visible — fullscreening the bare stage left the
  // user with no controls and, on phones, no visible way out.
  const toggleFullscreen = () => {
    // iOS: the player was created with playsinline=0 and always lives in the
    // OS's native fullscreen player — that IS its playback mode. From the
    // page, "fullscreen" just means resume (play reopens the native player
    // when Done was pressed). There is deliberately no rebuild-on-toggle:
    // rebuilding was the ping-pong bug.
    if (IS_IOS) {
      try { playerRef.current?.playVideo?.(); } catch { /* noop */ }
      setChromeVisible(true);
      return;
    }
    const el = pageRef.current;
    if (!el) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    if (el.requestFullscreen) {
      const p = el.requestFullscreen();
      // Gesture-less or blocked calls reject — flip the bars back on so the
      // button never feels dead.
      if (p?.catch) p.catch(() => setChromeVisible(true));
      return;
    }
    if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
      return;
    }
    setChromeVisible(true);
  };

  // Show the bars when entering/leaving fullscreen so they don't come back
  // hidden.
  useEffect(() => {
    const onChange = () => setChromeVisible(true);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // YouTube-style tap gestures on the stage (same spec as the other
  // players): double-tap left/right seeks ∓10s with a ripple, double-tap
  // center toggles fullscreen, center tap plays/pauses, other single taps
  // show/hide the chrome bars.
  const handleStageTap = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    const relX = rect ? (e.clientX - rect.left) / rect.width : 0.5;
    const zone = relX < GESTURE_EDGE ? "left" : relX > 1 - GESTURE_EDGE ? "right" : "center";
    const now = Date.now();
    const last = lastTapRef.current;
    const isDouble = now - last.t < 320 && Math.abs(e.clientX - last.x) < 60 && Math.abs(e.clientY - last.y) < 60;
    clearTimeout(last.timer);
    lastTapRef.current = { t: now, x: e.clientX, y: e.clientY, timer: null };
    if (isDouble) {
      lastTapRef.current.t = 0; // consumed — a third tap starts a new sequence
      if (zone === "left") {
        skip(-10);
        clearTimeout(rippleTimerRef.current);
        setRipple({ side: "left", label: "-10s" });
        rippleTimerRef.current = setTimeout(() => setRipple(null), 650);
      } else if (zone === "right") {
        skip(10);
        clearTimeout(rippleTimerRef.current);
        setRipple({ side: "right", label: "+10s" });
        rippleTimerRef.current = setTimeout(() => setRipple(null), 650);
      } else {
        toggleFullscreen();
      }
      return;
    }
    if (zone === "center") {
      togglePlay();
    } else {
      // Wait out the double-tap window, then show/hide the bars.
      last.timer = setTimeout(() => {
        setChromeVisible((c) => {
          if (c) {
            lastActivityRef.current = 0; // let the idle hider take over
            return false;
          }
          lastActivityRef.current = Date.now();
          return true;
        });
      }, 280);
    }
  };

  useEffect(
    () => () => {
      clearTimeout(lastTapRef.current?.timer);
      clearTimeout(rippleTimerRef.current);
      autoplayRetryRef.current.forEach(clearTimeout);
    },
    [],
  );

  const seekFromPointer = (clientX) => {
    const el = seekRef.current;
    const p = playerRef.current;
    if (!el || !p?.getDuration) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    p.seekTo(ratio * (p.getDuration() || 0), true);
  };

  const pct = time.dur > 0 ? Math.min(100, (time.cur / time.dur) * 100) : 0;

  // Layout mirrors the Vidking embed: solid black top bar (back + centered
  // title), the video between the bars, solid black bottom bar (times +
  // seekbar + icon row). When idle during playback the bars fade and only
  // the back arrow stays, same as Vidking.
  return (
    <div
      className="ytv-page"
      ref={pageRef}
      onPointerMove={showChrome}
      onPointerDown={showChrome}
    >
      <header className={`ytv-top${chromeVisible ? "" : " ytv-bar-hidden"}`}>
        {onBack && (
          <button className="ytv-back" onClick={onBack} aria-label="Back">
            <Ico.Back />
          </button>
        )}
        <div className="ytv-title">{request.title}</div>
      </header>

      <div className="ytv-stage" ref={containerRef}>
        {/* Fill mode is permanent: the iframe covers the whole stage, so the
            movie always uses the entire screen (top/bottom edges sit under
            the mask blocks). */}
        <div className="ytv-frame ytv-frame-fill">
          <div className="ytv-mount" ref={mountRef} />
        </div>
        {/* Corner masks cover YouTube's own overlays (title card, More-videos
            card, logo) inside the stage — our chrome lives outside the video. */}
        <div className="yt-mask yt-mask-top" aria-hidden="true" />
        <div className="yt-mask yt-mask-bottom" aria-hidden="true" />
        <div className="ytv-click" onClick={handleStageTap} aria-hidden="true" />
        {ripple?.side === "left" && (
          <div className="poku-ripple show" style={{ left: 0 }}>
            <Ico.SkipBack />
            <span>{ripple.label}</span>
          </div>
        )}
        {ripple?.side === "right" && (
          <div className="poku-ripple show" style={{ right: 0 }}>
            <Ico.SkipFwd />
            <span>{ripple.label}</span>
          </div>
        )}
      </div>

      <footer className={`ytv-bottom${chromeVisible ? "" : " ytv-bar-hidden"}`}>
        <div className="ytv-seek-row" onClick={(e) => e.stopPropagation()}>
          <span className="ytv-time">{fmt(time.cur)}</span>
          <div
            className="ytv-seek"
            ref={seekRef}
            onPointerDown={(e) => {
              draggingRef.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              seekFromPointer(e.clientX);
            }}
            onPointerMove={(e) => {
              if (draggingRef.current) seekFromPointer(e.clientX);
            }}
            onPointerUp={() => {
              draggingRef.current = false;
            }}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.floor(time.dur)}
            aria-valuenow={Math.floor(time.cur)}
          >
            <div className="ytv-seek-track" />
            <div className="ytv-seek-fill" style={{ width: `${pct}%` }} />
            <div className="ytv-seek-knob" style={{ left: `${pct}%` }} />
          </div>
          <span className="ytv-time">{fmt(time.dur)}</span>
        </div>
        <div className="ytv-row" onClick={(e) => e.stopPropagation()}>
          <div className="ytv-group">
            <button
              className="ytv-btn ytv-btn-pause"
              onClick={togglePlay}
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Ico.Pause /> : <Ico.Play />}
            </button>
            <button className="ytv-btn" onClick={() => skip(-10)} aria-label="Back 10 seconds">
              <Ico.SkipBack />
            </button>
            <button className="ytv-btn" onClick={() => skip(10)} aria-label="Forward 10 seconds">
              <Ico.SkipFwd />
            </button>
            <div className="ytv-volume" onClick={(e) => e.stopPropagation()}>
              <button className="ytv-btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                {muted || volume === 0 ? <Ico.VolX /> : <Ico.Vol />}
              </button>
              <div
                className="ytv-vol-slider"
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  volumeFromPointer(e.currentTarget, e.clientX);
                }}
                onPointerMove={(e) => {
                  if (e.buttons > 0) volumeFromPointer(e.currentTarget, e.clientX);
                }}
                role="slider"
                aria-label="Volume"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={muted ? 0 : volume}
              >
                <div className="ytv-vol-track">
                  <div className="ytv-vol-fill" style={{ width: `${muted ? 0 : volume}%` }} />
                  <div className="ytv-vol-knob" style={{ left: `${muted ? 0 : volume}%` }} />
                </div>
              </div>
              <span className="ytv-vol-pct">{muted ? 0 : volume}%</span>
            </div>
          </div>
          <div className="ytv-group">
            <button className="ytv-btn" onClick={toggleFullscreen} aria-label="Fullscreen">
              <Ico.Full />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
